/**
 * MIDDLEWARE MIXIN - IMPLEMENTATION RESTRICTIONS
 * 
 * 1. NO LOGIC CHANGES: Follow exact API flow from LOGIN_FLOW_DOCUMENTATION.md
 * 2. NO SYNTAX ERRORS: Ensure all JavaScript syntax is correct
 * 3. NO ASSUMPTIONS: Use only documented parameters and responses
 * 4. PRESERVE VARIABLES: Keep all existing variable names except uuid (now handled server-side)
 * 5. MAINTAIN API HANDLING: Same error handling, response processing, state management
 * 6. SESSION ONLY: Only add sessionId management, no other modifications
 */

import axios from 'axios'
import Swal from 'sweetalert2'
import sessionManager from '@/services/sessionManager'

export default {
  data() {
    return {
      middlewareUrl: 'https://devhub.rezcomm.com/hubapmydbauth/mmb/middleware.php'
    }
  },

  computed: {
    sessionId() {
      return sessionManager.sessionId
    }
  },

  methods: {
    /**
     * Email verification and session creation
     * Action: 'exist'
     */
    async checkUserExists(email, brandId) {
      try {
        const response = await axios.post(this.middlewareUrl, {
          action: 'exist',
          brand_id: brandId,
          email_id: email,
          ip: null,
          user_agent: navigator.userAgent
        })

        if (response.data.sessionId) {
          sessionManager.createSession(response.data.sessionId, brandId, email)
          console.log('🔑 INITIAL SESSION CREATED:', response.data.sessionId)
        }

        return response.data
      } catch (error) {
        this.handleMiddlewareError(error)
        throw error
      }
    },

    /**
     * Send OTP via middleware
     * Action: 'send-otp'
     */
    async sendOTPViaMiddleware(email, brandId, language = 'en', otpType = 'email') {
      try {
        if (!sessionManager.hasSession()) {
          throw new Error('No active session')
        }

        const response = await axios.post(this.middlewareUrl, {
          action: 'send-otp',
          brand_id: brandId,
          email_id: email,
          language: language,
          sessionId: sessionManager.sessionId,
          otp_type: otpType
        })

        // Update session if newSessionId provided
        if (response.data.newSessionId) {
          sessionManager.updateSession(response.data.newSessionId)
        }

        return response.data
      } catch (error) {
        this.handleMiddlewareError(error)
        throw error
      }
    },

    /**
     * Login authentication via middleware
     * Action: 'login'
     */
    async loginViaMiddleware(email, otpCode, brandId, otpType = 'email') {
      try {
        if (!sessionManager.hasSession()) {
          throw new Error('No active session')
        }

        const response = await axios.post(this.middlewareUrl, {
          action: 'login',
          brand_id: brandId,
          login_id: email,
          otp_code: otpCode,
          sessionId: sessionManager.sessionId,
          otp_type: otpType
        })

        // Session regeneration after successful login
        if (response.data.newSessionId) {
          console.log('🔄 SESSION UPDATED FROM:', sessionManager.sessionId, 'TO:', response.data.newSessionId)
          sessionManager.updateSession(response.data.newSessionId)
          console.log('✅ NEW SESSION STORED:', sessionManager.sessionId)
        }

        return response.data
      } catch (error) {
        this.handleMiddlewareError(error)
        throw error
      }
    },

    /**
     * Get user details via middleware
     * Action: 'get-profile-data'
     */
    async getUserDetailsViaMiddleware(brandId, email) {
      try {
        if (!sessionManager.hasSession()) {
          throw new Error('No active session')
        }

        const response = await axios.post(this.middlewareUrl, {
          action: 'get-profile-data',
          brand_id: brandId,
          email_id: email,
          sessionId: sessionManager.sessionId
        })

        // Update session if newSessionId provided
        if (response.data.newSessionId) {
          sessionManager.updateSession(response.data.newSessionId)
        }

        return response.data
      } catch (error) {
        this.handleMiddlewareError(error)
        throw error
      }
    },

    /**
     * Get OTP preference for LAX brand
     * Action: 'get-otp-preference'
     */
    async getOTPPreference(brandId, email) {
      try {
        if (!sessionManager.hasSession()) {
          throw new Error('No active session')
        }

        const response = await axios.post(this.middlewareUrl, {
          action: 'get-otp-preference',
          brand_id: brandId,
          email_id: email,
          sessionId: sessionManager.sessionId
        })

        // Update session if newSessionId provided
        if (response.data.newSessionId) {
          sessionManager.updateSession(response.data.newSessionId)
        }

        return response.data
      } catch (error) {
        this.handleMiddlewareError(error)
        throw error
      }
    },



    /**
     * Error handling for middleware calls
     */
    handleMiddlewareError(error) {
      console.error('Middleware Error:', error)
      
      if (error.response?.data?.error === 'SESSION_EXPIRED' || 
          error.response?.data?.error === 'SESSION_INVALID') {
        sessionManager.clearSession()
      }

      const errorMessage = error.response?.data?.msg || 
                          error.response?.data?.error || 
                          'Network error occurred. Please try again.'
      
      Swal.fire({
        title: "Alert",
        icon: "error",
        text: errorMessage,
        confirmButtonText: "Close"
      })
    }
  },

  /**
   * Initialize session on component mount
   */
  mounted() {
    sessionManager.init()
    console.log('🚀 PAGE LOADED - SessionId:', sessionManager.sessionId || 'NO SESSION')
  }
}